import { useState } from "react";
import { Check, Calculator } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { TIER_META } from "./data";

export const Pricing = ({ onInitiate }) => {
  const { t } = useLanguage();
  const [claim, setClaim] = useState("");
  const value = parseFloat(claim);
  const matched = Number.isFinite(value) && value > 0
    ? TIER_META.findIndex((m, i) => value <= m.max && (i === 0 || value > TIER_META[i - 1].max))
    : -1;

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal><Overline className="justify-center">{t.pricing.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight text-slate-50 sm:text-4xl lg:text-5xl">
              {t.pricing.titleA} <span className="italic text-gold-gradient">{t.pricing.titleB}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 leading-relaxed text-slate-400">{t.pricing.body}</p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="mx-auto mt-12 flex max-w-xl flex-col items-center gap-3 border border-navy-border bg-navy-card/50 px-6 py-6 sm:flex-row">
            <label htmlFor="claim-value-input" className="flex items-center gap-2 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
              <Calculator className="h-4 w-4" /> {t.pricing.calcLabel}
            </label>
            <input
              id="claim-value-input" data-testid="claim-value-input" type="number" min="0"
              placeholder={t.pricing.calcPlaceholder} value={claim} onChange={(e) => setClaim(e.target.value)}
              className="w-full border-b border-navy-border bg-transparent px-2 py-2 text-center font-serif text-2xl text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-gold"
            />
            <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="claim-value-result">
              {matched >= 0 ? `${t.pricing.tierPrefix}: ${t.pricing.tiers[matched].name}` : t.pricing.calcEmpty}
            </span>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {t.pricing.tiers.map((tier, i) => {
            const meta = TIER_META[i];
            const isMatched = matched === i;
            return (
              <Reveal key={tier.name} delay={i * 0.1}>
                <div data-testid={`pricing-tier-${i}`}
                  className={`relative flex h-full flex-col border p-8 transition-all duration-500 lg:p-10 ${
                    isMatched
                      ? "border-gold bg-navy-card shadow-[0_0_60px_rgba(212,175,55,0.12)]"
                      : meta.highlight
                        ? "border-gold/40 bg-navy-card/80"
                        : "border-navy-border bg-navy-card/40 hover:border-gold/30"
                  }`}>
                  {(meta.highlight || isMatched) && (
                    <span className="absolute -top-3 left-8 bg-gold px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-deep" data-testid={`pricing-badge-${i}`}>
                      {isMatched ? t.pricing.matched : t.pricing.mostRetained}
                    </span>
                  )}
                  <h3 className="font-serif text-2xl font-medium text-slate-100">{tier.name}</h3>
                  <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{tier.range}</p>
                  <div className="mt-7 flex items-baseline gap-2">
                    <span className="font-serif text-5xl font-semibold text-gold">{meta.fee}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{tier.feeSuffix}</span>
                  </div>
                  <p className="mt-2 text-sm italic text-slate-400">{tier.timeframe}</p>
                  <div className="my-7 h-px bg-navy-border" />
                  <ul className="flex-1 space-y-3.5">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={onInitiate} data-testid={`pricing-cta-${i}`}
                    className={`mt-9 w-full border py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                      isMatched || meta.highlight
                        ? "border-gold bg-gold text-navy-deep hover:bg-gold-hover"
                        : "border-gold/40 text-gold hover:bg-gold hover:text-navy-deep"
                    }`}>
                    {t.pricing.cta}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
